/**
 * peerEngine.js — Peer comparison benchmarks.
 *
 * Pure functions — zero React.
 * Uses hardcoded Canadian financial benchmarks by age group
 * (sourced from StatsCan Survey of Financial Security,
 * Wealthsimple/Ipsos reports, and Canadian financial planning data).
 *
 * All numbers are approximate medians and percentiles for
 * Canadian university-educated adults. These are intentionally
 * conservative to make "beating" the benchmarks achievable
 * and motivating for the target audience.
 */

import { FF } from '../../utils/format';

/* ═══════════════════════════════════════════════════
   BENCHMARK DATA BY AGE BRACKET
   ═══════════════════════════════════════════════════
   Each bracket has:
     range:    display label
     min/max:  age bounds (inclusive)
     nw:       { p25, p50, p75 } — net worth percentiles
     savings:  { p25, p50, p75 } — annual savings percentiles
     income:   { p25, p50, p75 } — gross income percentiles
     rate:     { p25, p50, p75 } — savings rate percentiles
*/

const BENCHMARKS = [
  {
    range: "18–24",
    min: 18, max: 24,
    nw:      { p25: -5000,  p50: 2000,   p75: 15000 },
    savings: { p25: 0,      p50: 3000,   p75: 10000 },
    income:  { p25: 15000,  p50: 28000,  p75: 45000 },
    rate:    { p25: 0,      p50: 5,      p75: 18 },
  },
  {
    range: "25–29",
    min: 25, max: 29,
    nw:      { p25: 1000,   p50: 18000,  p75: 65000 },
    savings: { p25: 2000,   p50: 8000,   p75: 25000 },
    income:  { p25: 40000,  p50: 55000,  p75: 80000 },
    rate:    { p25: 3,      p50: 10,     p75: 22 },
  },
  {
    range: "30–34",
    min: 30, max: 34,
    nw:      { p25: 10000,  p50: 55000,  p75: 180000 },
    savings: { p25: 3000,   p50: 12000,  p75: 35000 },
    income:  { p25: 48000,  p50: 68000,  p75: 100000 },
    rate:    { p25: 4,      p50: 12,     p75: 25 },
  },
  {
    range: "35–39",
    min: 35, max: 39,
    nw:      { p25: 25000,  p50: 100000, p75: 350000 },
    savings: { p25: 4000,   p50: 15000,  p75: 45000 },
    income:  { p25: 52000,  p50: 75000,  p75: 115000 },
    rate:    { p25: 5,      p50: 13,     p75: 27 },
  },
  {
    range: "40–44",
    min: 40, max: 44,
    nw:      { p25: 40000,  p50: 150000, p75: 500000 },
    savings: { p25: 5000,   p50: 18000,  p75: 50000 },
    income:  { p25: 55000,  p50: 80000,  p75: 125000 },
    rate:    { p25: 5,      p50: 14,     p75: 28 },
  },
];

/**
 * Find the benchmark bracket for a given age.
 */
function getBracket(age) {
  return BENCHMARKS.find((b) => age >= b.min && age <= b.max) || BENCHMARKS[0];
}

/**
 * Estimate percentile rank given a value and p25/p50/p75 benchmarks.
 * Uses linear interpolation between known percentile points.
 * Returns 0–99.
 */
function estimatePercentile(value, benchmarks) {
  const { p25, p50, p75 } = benchmarks;

  if (value <= p25) {
    // Below 25th — interpolate 0–25
    const floor = p25 - (p50 - p25); // estimate ~p0
    if (value <= floor) return 1;
    return Math.round(25 * ((value - floor) / (p25 - floor)));
  }

  if (value <= p50) {
    // 25th to 50th
    return Math.round(25 + 25 * ((value - p25) / (p50 - p25)));
  }

  if (value <= p75) {
    // 50th to 75th
    return Math.round(50 + 25 * ((value - p50) / (p75 - p50)));
  }

  // Above 75th — interpolate 75–99
  const ceiling = p75 + (p75 - p50); // estimate ~p100
  if (value >= ceiling) return 99;
  return Math.round(75 + 24 * ((value - p75) / (ceiling - p75)));
}

/**
 * Calculate peer comparison data for the user.
 *
 * @param {object} s - app state
 * @param {array} proj - projection array
 * @returns {object} comparison data
 */
export function calculatePeerComparison(s, proj) {
  const bracket = getBracket(s.age);
  const w = proj.find((d) => d.a >= s.startAge) || proj[0] || {};

  // Current values
  const nw = s.nw0 || 0;
  const income = w.comp || s.salary || 0;
  const savingsAnnual = w.sv || 0;
  const savingsRate = income > 0 ? Math.round((savingsAnnual / income) * 100) : 0;

  // Percentile ranks
  const nwPercentile = estimatePercentile(nw, bracket.nw);
  const incomePercentile = estimatePercentile(income, bracket.income);
  const savingsPercentile = estimatePercentile(savingsAnnual, bracket.savings);
  const ratePercentile = estimatePercentile(savingsRate, bracket.rate);

  // Build comparison items
  const comparisons = [
    {
      id: "nw",
      label: "Net Worth",
      value: FF(nw),
      percentile: nwPercentile,
      median: FF(bracket.nw.p50),
      aboveMedian: nw >= bracket.nw.p50,
    },
    {
      id: "income",
      label: "Income",
      value: FF(income),
      percentile: incomePercentile,
      median: FF(bracket.income.p50),
      aboveMedian: income >= bracket.income.p50,
    },
    {
      id: "savings",
      label: "Annual Savings",
      value: FF(savingsAnnual),
      percentile: savingsPercentile,
      median: FF(bracket.savings.p50),
      aboveMedian: savingsAnnual >= bracket.savings.p50,
    },
    {
      id: "rate",
      label: "Savings Rate",
      value: `${savingsRate}%`,
      percentile: ratePercentile,
      median: `${bracket.rate.p50}%`,
      aboveMedian: savingsRate >= bracket.rate.p50,
    },
  ];

  // Overall score (average percentile)
  const overallPercentile = Math.round(
    (nwPercentile + incomePercentile + savingsPercentile + ratePercentile) / 4
  );

  return {
    bracket,
    comparisons,
    overallPercentile,
  };
}

/**
 * Get a contextual message based on peer comparison.
 */
export function getPeerMessage(data) {
  const { overallPercentile, bracket } = data;

  if (overallPercentile >= 90) {
    return `Top 10% of Canadians aged ${bracket.range}. You're not just ahead — you're in a different league.`;
  }
  if (overallPercentile >= 75) {
    return `Top 25% of Canadians aged ${bracket.range}. The decisions you're making now are already separating you from the pack.`;
  }
  if (overallPercentile >= 60) {
    return `Ahead of most Canadians aged ${bracket.range}. Your consistency is building an advantage that compounds every year.`;
  }
  if (overallPercentile >= 50) {
    return `Above median for Canadians aged ${bracket.range}. You're already doing better than half your peers — keep building.`;
  }
  if (overallPercentile >= 35) {
    return `Building momentum among Canadians aged ${bracket.range}. The fact that you're planning puts you ahead of where the numbers show.`;
  }
  return `Early in your journey. Most wealth is built in your late 20s and 30s — the runway ahead is what matters.`;
}
